import { apiCall } from '../api';
import { getUser } from '../auth';

interface Announcement {
  id: string;
  subject: string;
  body: string;
  priority: 'low' | 'normal' | 'high' | 'urgent' | 'critical';
  active: boolean;
  created_at: string;
  updated_at: string;
  active_until?: string;
  groups: string[];
}

interface AnnouncementTracker {
  id: string;
  announcement_id: string;
  username: string;
  status: 'active' | 'dismissed';
  dismissed_at?: string;
  announcement?: Announcement;
}

interface UserAnnouncementsResponse {
  username: string;
  announcements: AnnouncementTracker[];
}

export async function loadAnnouncements(): Promise<void> {
  // Check if user is authenticated first
  const user = await getUser();
  if (!user) {
    // Clear any existing announcements if user is not logged in
    const container = document.getElementById('announcements-container');
    if (container) {
      container.innerHTML = '';
    }
    return;
  }

  const container = document.getElementById('announcements-container');
  if (!container) {
    console.warn('Announcements container not found in DOM');
    return;
  }

  try {
    const data = await apiCall<UserAnnouncementsResponse>('GET', '/api/announcements/');
    
    // Clear existing announcements
    container.innerHTML = '';
    
    // Filter for active announcements that haven't been dismissed
    const activeAnnouncements = data.announcements.filter(
      tracker => tracker.status === 'active' && tracker.announcement?.active,
    );
    
    if (activeAnnouncements.length === 0) {
      return;
    }
    
    // Create announcements UI
    activeAnnouncements.forEach(tracker => {
      if (!tracker.announcement) return;
      
      const announcement = tracker.announcement;
      const alertElement = document.createElement('sl-alert');
      alertElement.setAttribute('open', '');
      alertElement.setAttribute('closable', '');
      
      // Set variant based on priority
      const variantMap: Record<string, string> = {
        'low': 'primary',
        'normal': 'primary',
        'high': 'warning',
        'urgent': 'warning',
        'critical': 'danger',
      };
      alertElement.setAttribute('variant', variantMap[announcement.priority] || 'primary');
      
      // Create content
      const titleElement = document.createElement('strong');
      titleElement.textContent = announcement.subject;
      
      const messageElement = document.createElement('div');
      messageElement.style.marginTop = '0.5rem';
      messageElement.textContent = announcement.body;
      
      alertElement.appendChild(titleElement);
      alertElement.appendChild(messageElement);
      
      // Handle dismiss
      alertElement.addEventListener('sl-after-hide', async () => {
        try {
          await apiCall('PATCH', `/api/announcements/trackers/${tracker.id}/dismiss`);
          // Remove from DOM
          alertElement.remove();
        } catch (e) {
          console.error('Failed to dismiss announcement:', e);
          // Show the alert again if dismiss failed
          alertElement.setAttribute('open', '');
        }
      });
      
      container.appendChild(alertElement);
    });
  } catch (e: unknown) {
    console.error('Failed to load announcements:', e);
  }
}

export async function initializeAnnouncements(): Promise<void> {
  // Load announcements when authentication state changes
  window.addEventListener('auth-state-changed', async () => {
    await loadAnnouncements();
  });

  // Load announcements initially only if user is authenticated
  await loadAnnouncements();
}